import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { RequestStatus } from '@magimp/shared';
import { fetchRequest } from '../../api/requests/index.js';
import { useAuthStore } from '../../stores/auth.js';
import { useToastStore } from '../../stores/toast.js';
import { detectRoleKind } from './role.js';
import { requestsKeys } from './queries.js';
import {
  nextClientBoxId,
  nextClientBoxItemId,
  nextClientPackageId,
  useRequestsStore,
} from './store.js';
import { useRequestUrlState } from './useRequestUrlState.js';
import { useTryCloseDraft } from './useTryCloseDraft.js';
import type { RequestView, UserRef } from './types.js';

// WHY: shared by desktop ListRow and mobile RequestMobileCard so a click
// (or Enter/Space) on either surface runs the identical DRAFT-edit-vs-
// RequestDrawer routing logic, including the dirty-close guard. Lifting
// this also keeps the mobile branch from drifting away from the desktop
// branch when draft-like routing rules change.
export function useOpenRequestRow(): (r: RequestView) => void {
  const { t } = useTranslation('requests');
  const user = useAuthStore((s) => s.user);
  const showToast = useToastStore((s) => s.show);
  const role = detectRoleKind(user);
  const openPersistedDraft = useRequestsStore((s) => s.openPersistedDraft);
  const urlState = useRequestUrlState();
  const tryCloseDraft = useTryCloseDraft();
  const qc = useQueryClient();

  return useCallback(
    (r: RequestView) => {
      const isDraftLike = r.status === RequestStatus.DRAFT || r.status === RequestStatus.RETURN;
      const canEdit = isDraftLike && user !== null && (role === 'admin' || r.managerId === user.id);
      if (canEdit && user !== null) {
        const userId = user.id;
        const userName = user.name;
        const detectedRole = detectRoleKind(user);
        const rowStatus = r.status as typeof RequestStatus.DRAFT | typeof RequestStatus.RETURN;
        const rowReturnReason = r.returnReason;
        tryCloseDraft(() => {
          const me: UserRef = { id: userId, name: userName, role: detectedRole };
          // WHY (#590): list responses omit package detail to keep the
          //   bundle minimal; opening a persisted DRAFT/RETURN in the
          //   editor needs the full package shape so dimensions-mode
          //   items keep their rows. Fetch the expanded detail via
          //   queryClient.fetchQuery so the same data also primes the
          //   cache for any follow-up `useRequest` consumer.
          // WHY (#1597): this prefetch primes the shared detail cache key,
          //   so it mirrors `includeFunnelLinks` to keep the lead/deal chips
          //   coherent when the drawer reads the primed entry (AC-4).
          void qc
            .fetchQuery({
              queryKey: requestsKeys.detail(r.id),
              queryFn: ({ signal }) =>
                fetchRequest(r.id, {
                  signal,
                  includeItemPackages: true,
                  includeBoxes: true,
                  includeFunnelLinks: true,
                }),
            })
            .then((detail) => {
              const persistedBoxes = (detail.boxes ?? []).map((b) => ({
                id: b.id,
                clientBoxId: nextClientBoxId(),
                count: b.count,
                weightKg: b.weightKg,
                volumeM3: b.volumeM3,
                volumeMode: b.volumeMode,
                widthCm: b.widthCm,
                depthCm: b.depthCm,
                heightCm: b.heightCm,
                items: b.items.map((it) => ({
                  id: it.id,
                  clientBoxItemId: nextClientBoxItemId(),
                  // WHY (#704): preserve persisted group assignment
                  //   across reopen → save round-trip.
                  groupOrdinal: it.groupOrdinal,
                  name: it.name,
                  brand: it.brand,
                  material: it.material ?? '',
                  description: it.description,
                  quantity: it.quantity,
                  photos: [...it.photos],
                })),
              }));
              openPersistedDraft({
                row: {
                  id: detail.id,
                  manager: me,
                  name: detail.name,
                  // WHY (#701): hydrate the kind + transportTypeId from
                  //   the wire so a re-opened persisted CONTAINER draft
                  //   keeps its discriminator.
                  kind: detail.kind,
                  transportTypeId: detail.transportTypeId,
                  route: detail.route,
                  destinations: detail.destinations.map((dest) => ({
                    arrivalCityId: dest.arrivalCityId,
                    sortOrder: dest.sortOrder,
                  })),
                  // WHY (#1057): hydrate the persisted CONTAINER delivery
                  //   address so a re-opened draft keeps the typed value.
                  //   STANDARD rows always carry `null` here.
                  deliveryAddress: detail.deliveryAddress,
                  // WHY (#1393): hydrate the persisted CONTAINER pickup
                  //   address so a re-opened draft keeps the typed value.
                  //   Non-CONTAINER rows always carry `null` here.
                  pickupAddress: detail.pickupAddress,
                  // WHY (#1061): hydrate the insurance triplet from the
                  //   wire detail so a re-opened insured draft does not
                  //   silently reset the toggle and erase the cargo
                  //   value/currency on the next Save. The wire shape
                  //   carries these as required fields on every detail
                  //   response.
                  insuranceRequested: detail.insuranceRequested,
                  insuranceCargoValue: detail.insuranceCargoValue,
                  insuranceCargoCurrency: detail.insuranceCargoCurrency,
                  // WHY (#996): hydrate Roads/Packaging selections from
                  //   the persisted detail. Inactive ids are preserved
                  //   so the editor displays historical selections; the
                  //   user must remove them explicitly to drop them.
                  roadOptionIds: (detail.roads ?? []).map((r) => r.id),
                  packagingOptionIds: (detail.packaging ?? []).map((p) => p.id),
                  // WHY (#1448): hydrate the STANDARD request-level totals
                  //   from the #1447 read DTO. Legacy STANDARD rows that
                  //   predate the backfill carry `null`; the editor then
                  //   surfaces empty fields and the gate blocks Send.
                  totalWeight: detail.weight,
                  totalVolume: detail.volume,
                  // WHY (#996): preserve the original `{id,name,active,sortOrder}`
                  //   projection alongside the ids so the editor can
                  //   render inactive historical chips by name + muted
                  //   styling. Active rows are still re-fetched from the
                  //   live catalog query.
                  roadOptionProjections: (detail.roads ?? []).map((r) => ({
                    id: r.id,
                    name: r.name,
                    active: r.active,
                    sortOrder: r.sortOrder,
                  })),
                  packagingOptionProjections: (detail.packaging ?? []).map((p) => ({
                    id: p.id,
                    name: p.name,
                    active: p.active,
                    sortOrder: p.sortOrder,
                  })),
                  items: detail.items.map((it) => ({
                    id: it.id,
                    name: it.name,
                    brand: it.brand,
                    material: it.material ?? '',
                    // WHY (#701): nullable on the wire — preserve null
                    //   when present rather than coercing to 0 so the
                    //   CONTAINER mode round-trips its actual state.
                    weight: it.weight,
                    volume: it.volume,
                    quantity: it.quantity,
                    // WHY (#1056): wire is optional+nullable → coalesce
                    //   `undefined` (legacy bundle) to `null` so the
                    //   scratchpad has one absent sentinel.
                    productQuantity: it.productQuantity ?? null,
                    description: it.description,
                    photos: [...it.photos],
                    dimensionsMode: it.dimensionsMode === true,
                    packages: (it.packages ?? []).map((p) => ({
                      id: p.id,
                      clientPackageId: nextClientPackageId(),
                      widthCm: p.widthCm,
                      depthCm: p.depthCm,
                      heightCm: p.heightCm,
                      weightKg: p.weightKg,
                      count: p.count,
                    })),
                  })),
                  // WHY (#599): seed scratchpad with persisted boxes
                  //   so reopening a DRAFT/RETURN preserves them.
                  //   Mark the section open so the next save echoes
                  //   the boxes field (otherwise a no-op edit would
                  //   silently drop persisted boxes).
                  boxes: persistedBoxes,
                  boxesSectionOpen: persistedBoxes.length > 0,
                  clientToken: '',
                  status: rowStatus,
                  returnReason: rowReturnReason,
                },
                manager: me,
              });
            })
            .catch(() => {
              // WHY (#590): a transient detail-fetch failure must NOT
              //   open the editor from the list-shape payload — the
              //   list omits `dimensionsMode` and `packages`, so the
              //   first save would reconcile the missing rows away and
              //   permanently delete the persisted package data. Surface
              //   the failure as a retryable toast and leave the URL
              //   state untouched.
              showToast({ tone: 'error', text: t('errors.detailFetchFailed') });
            });
        });
        return;
      }
      urlState.openRequest(r.id);
    },
    [user, role, tryCloseDraft, openPersistedDraft, urlState, qc, showToast, t],
  );
}
